import type { Theme } from "~/common/mui";
import { C_OUTLINE_VARIANT } from "~/common/components/icon/Colors";

export const orderSummaryStyles = (theme: Theme) => ({
  ".scha__order-summary": {
    marginBottom: "60px",
    "&__param-column": {
      "& > * + *": {
        marginTop: "10px",
      },
    },
    "&__params": {
      flex: 2,
    },
    "&__totals": {
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-end",
      "&__actions": {
        display: "flex",
        justifyContent: "flex-end",
      },
    },
    "&__comment": {
      wordBreak: "break-all",
    },
  },
});

export const orderHistoryStyles = (theme: Theme) => ({
  ".scha__orders__overview-table, .scha__quotes__overview-table": {
    borderBottom: `1px solid ${C_OUTLINE_VARIANT}`,
  },
  "tr.scha__orders__overview-table__row": {
    background: "transparent !important",
  },
  "th.scha__orders__overview-table__cell, td.scha__orders__overview-table__cell": {
    padding: "10px",
  },
  "tr.scha__orders__overview-table__details-row, tr.scha__orders__overview-table__spacer": {
    display: "none",
    "&.open": {
      display: "table-row",
    },
  },
  "tr.scha__orders__overview-table__spacer": {
    "&.open": {
      background: "none !important",
    },
  },
  "td.scha__orders__overview-table__details-cell": {
    border: `1px solid ${C_OUTLINE_VARIANT}`,
    borderTop: "0px",
    paddingBottom: "10px",
    background: "white",
    borderRadius: "0px 0px 4px 4px",
  },
  "td.scha__orders__overview-table__upper-cell": {
    borderTop: `1px solid ${C_OUTLINE_VARIANT}`,
    "&:first-child": {
      borderLeft: "1px solid transparent",
    },
    "&:last-child": {
      borderRight: "1px solid transparent",
    },
    "tr.scha__orders__overview-table__upper-row.open &": {
      borderColor: C_OUTLINE_VARIANT,
    },
  },
  ".scha__orders__overview-table__buttons": {
    textAlign: "right !important",
    padding: "10px !important",
  },
  ".scha__orders__overview-table__open-close-button": {
    display: "flex",
    justifyContent: "flex-end",
    gap: "23px",
    "& i": {
      lineHeight: "18px",
    },
  },
  ".scha__order-list__deliveries": {
    borderBottom: `1px solid ${theme.palette.text.primary} !important`,
    ...theme.typography.labelLarge,
    color: `${theme.palette.text.secondary} !important`,
  },
  ".scha__order-list__deliveries-case-row": {
    "& td": {
      borderBottom: `1px solid ${C_OUTLINE_VARIANT} !important`,
    },
    "& td:first-child": {
      borderBottom: "unset !important",
    },
  },
  ".scha__order-list__deliveries-row-spacer": {
    height: "16px",
  },
  ".scha__order-list-nota-fiscal": {
    marginTop: "-10px",
  },
});

export const orderDetailsStyles = (theme: Theme) => ({
  ".scha__order-table": {
    "&__header": {
      display: "flex",
      flexDirection: "column",
      "&__title": {
        paddingBottom: "4px",
        "&:not(&--light)": {
          fontWeight: "bold",
        },
      },
      "&__subtitle": {
        color: theme.palette.text.secondary,
        fontWeight: "normal",
      },
    },
    "&__product": {
      paddingRight: "30px",
      "&__description": {
        paddingTop: "10px",
      },
    },
    "&__tracker": {
      "&__link": {
        paddingTop: "5px",
      },
    },
    "& th": {
      ...theme.typography.labelMedium,
      fontWeight: "bold",
      color: "black",
      textTransform: "none",
      whiteSpace: "pre-line",
      "&:last-child": {
        textAlign: "left",
      },
    },
    "& tbody td": {
      position: "relative",
    },
    "& tbody td:last-child": {
      position: "relative",
      padding: "10px 8px 10px 16px",
    },
    "& .scha__order-table__tracker": {
      "& .scha__order-table__tracker__icon": {
        marginRight: "0px",
        marginTop: "-2px",
        fontSize: "12px",
      },
    },
  },
  ".scha__order-details-display": {
    "&__totals": {
      marginTop: "60px",
      width: "calc(100% / 3)",
      "&--wrapper": {
        borderTop: `3px solid ${C_OUTLINE_VARIANT}`,
        display: "flex",
        justifyContent: "flex-end",
      },
    },
  },
  ".scha__order-totals__row": {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "&:not(:last-child)": {
      paddingBottom: "10px",
    },
  },
  "tr.scha__order-details-page-shipment-row": {
    backgroundColor: "white !important",
    "& .scha__order-table__tracker": {
      "& .scha__order-table__tracker__icon": {
        marginRight: "0px",
        marginTop: "-2px",
        fontSize: "12px",
      },
    },
  },
  "tr.scha__order-details-page-shipment-row--last": {
    borderBottom: `1px solid ${C_OUTLINE_VARIANT}`,
    "& > .MuiTableCell-body": {
      border: "inherit",
    },
  },
  "td.scha__order-details-page-shipment-row__cell__package-info": {
    borderBottom: `1px solid ${C_OUTLINE_VARIANT}`,
  },
  ".scha__order-table-print__header": {
    display: "flex",
    paddingBottom: "8px",
    borderBottom: `1px solid ${theme.palette.text.primary}`,
    marginBottom: "16px",
    "& > *": {
      flex: 1,
    },
    "&__item": {
      display: "flex",
      padding: "8px 0",
      "& > *": {
        flex: 1,
      },
    },
    "&__subItem": {
      display: "flex",
      padding: "16px 0",
      borderBottom: `1px solid ${C_OUTLINE_VARIANT}`,
      "& > *": {
        flex: 1,
      },
      "&:last-child": {
        borderBottom: "unset",
      },
    },
  },
  ".scha__order-details-display-print": {
    "&__orders": {
      "&__item": {
        borderBottom: `1px solid ${theme.palette.text.primary}`,
        marginBottom: "16px",
        paddingBottom: "8px",
        pageBreakInside: "avoid",
        "&:last-child": {
          borderBottom: "unset",
        },
        "& .scha__message.info a": {
          textDecoration: "none",
        },
        "& .scha__quote-table__main-quote": {
          marginTop: 0,
        },
        "& .scha__quote-table__header": {
          borderBottom: `1px solid ${theme.palette.text.primary}`,
          borderTop: `1px solid ${theme.palette.text.primary}`,
        },
        "&__header": {
          borderBottom: `1px dashed ${C_OUTLINE_VARIANT}`,
          paddingBottom: "16px",
        },
      },
    },
  },
});

export const orderPageStyles = (theme: Theme) => ({
  ...orderSummaryStyles(theme),
  ...orderHistoryStyles(theme),
  ...orderDetailsStyles(theme),
});
